import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { UserApi } from '../../services/api/UserApi';

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);           // full-page only on first load
  const [inlineLoading, setInlineLoading] = useState(false); // subsequent loads
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(''); // debounce driver
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState('userId');     // 'userId' | 'username' | 'roles' | 'recentLogin'
  const [sortDir, setSortDir] = useState('desc');     // 'asc' | 'desc'

  const currentRequestRef = useRef(null);

  // Debounce search input (350ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadUsers(page, size, sortBy, sortDir, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sortBy, sortDir, debouncedSearch]);

  const loadUsers = async (
    pageArg = page,
    sizeArg = size,
    sortByArg = sortBy,
    sortDirArg = sortDir,
    searchArg = debouncedSearch
  ) => {
    setError(null);

    // Abort previous in-flight request (if any)
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }
    const controller = new AbortController();
    currentRequestRef.current = controller;

    if (!hasLoadedOnce) setLoading(true);
    else setInlineLoading(true);

    try {
      const response = await UserApi.getAllUsers({
        page: pageArg,      // UI 1-based; service converts to 0-based
        size: sizeArg,
        sortBy: sortByArg,
        sortDir: sortDirArg,
        search: searchArg,
        signal: controller.signal,
      });

      if (!response.ok) {
        // If aborted, silently exit
        if (controller.signal.aborted) return;
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to load users');
      }

      const data = await response.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
      setTotalPages(Number(data.totalPages || 1));
      setTotalUsers(Number(data.totalUsers || (Array.isArray(data.users) ? data.users.length : 0)));

      // Normalize currentPage (server 0-based -> UI 1-based)
      if (typeof data.currentPage === 'number') {
        const uiPage = data.currentPage >= 0 ? (data.currentPage + 1) : data.currentPage;
        if (uiPage !== page) setPage(Number(uiPage));
      }

      if (!hasLoadedOnce) setHasLoadedOnce(true);
    } catch (err) {
      if (controller.signal.aborted) return; // ignore aborts
      console.error('UserList: Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setInlineLoading(false);
      }
    }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Are you sure you want to delete the user "${email}"? This action cannot be undone and will remove all their data.`)) {
      return;
    }
    try {
      const response = await UserApi.deleteUser(id);
      if (response.ok) {
        loadUsers(1, size, sortBy, sortDir, debouncedSearch); // refresh from first page after deletion
        setPage(1);
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user. Please try again.');
    }
  };

  const getStatusColor = (isActive) => (isActive ? '#28a745' : '#6c757d');

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    if (Number.isNaN(date.getTime())) return 'Never';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).format(date);
  };

  const filteredUsers = users; // server-side search

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const changePage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handlePageSizeChange = (e) => {
    const nextSize = Number(e.target.value);
    setSize(nextSize);
    setPage(1);
  };
  const toggleSortDir = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  const handleSortByChange = (e) => {
    setSortBy(e.target.value);
    setPage(1);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // reset to first page when searching
  };

  // Show full-page loader only on the very first load
  if (loading && !hasLoadedOnce) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <h2 style={{ marginRight: 'auto' }}>User Management</h2>
        {inlineLoading && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            <span className="loading-spinner" style={{ width: 16, height: 16 }} />
            Updating…
          </span>
        )}
        <Link to="/settings/users/create">
          <button type="button">Create New User</button>
        </Link>
      </div>
      
      {error && <div className="error-message">{error}</div>}

      <div className="search-container" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search users by name, email, or role..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ flex: 1 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label htmlFor="pageSize" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Rows:</label>
          <select id="pageSize" value={size} onChange={handlePageSizeChange}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label htmlFor="sortBy" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Sort:</label>
          <select id="sortBy" value={sortBy} onChange={handleSortByChange}>
            <option value="userId">ID</option>
            <option value="username">Email</option>
            <option value="roles">Roles</option>
            <option value="recentLogin">Recent Login</option>
          </select>
          <button type="button" onClick={toggleSortDir} title="Toggle sort direction">
            {sortDir === 'asc' ? 'Asc ▲' : 'Desc ▼'}
          </button>
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <h3>No users found</h3>
          <p>{debouncedSearch ? 'No users match your search.' : 'No users found. Create your first one!'}</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Status</th>
              <th>First Login</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.userId || user.user_id || user.id}>
                <td data-label="User">
                  <div>
                    <strong>
                      {(user.firstName || user.first_name) || (user.lastName || user.last_name)
                        ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim()
                        : user.email?.split('@')[0] || 'Unknown User'
                      }
                    </strong>
                    {user.organization && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        {user.organization.name || user.organization.organization_name || user.organization.organizationName}
                      </div>
                    )}
                  </div>
                </td>
                <td data-label="Email">
                  <a href={`mailto:${user.email}`} style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
                    {user.email}
                  </a>
                </td>
                <td data-label="Roles">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((role, index) => (
                        <span 
                          key={index}
                          style={{
                            padding: '0.125rem 0.375rem',
                            backgroundColor: (role.role_name || role.roleName) === 'admin' ? 'var(--color-brand-danger, #dc3545)' : 
                                           (role.role_name || role.roleName) === 'instructor' ? 'var(--color-brand-purple, #6f42c1)' : 'var(--color-gray-600, #6c757d)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}
                        >
                          {role.role_name || role.roleName}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        No roles assigned
                      </span>
                    )}
                  </div>
                </td>
                <td data-label="Status">
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    color: getStatusColor(user.isActive ?? user.is_active),
                    fontWeight: '500',
                    fontSize: '0.875rem'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: getStatusColor(user.isActive ?? user.is_active),
                      borderRadius: '50%'
                    }} />
                    {(user.isActive ?? user.is_active) !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td data-label="First Login">
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {formatLastLogin(user.firstLoginAt || user.first_login_at)}
                  </span>
                </td>
                <td data-label="Last Login">
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {formatLastLogin(user.lastLogin || user.lastLoginAt || user.last_login_at)}
                  </span>
                </td>
                <td data-label="Actions">
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link to={`/settings/users/${user.userId || user.user_id || user.id}`}>
                      Edit
                    </Link>
                    <Link to={`/settings/users/${user.userId || user.user_id || user.id}/roles`}>
                      Roles
                    </Link>
                    <Link to={`/settings/users/${user.userId || user.user_id || user.id}/organizations`}>
                      Orgs
                    </Link>
                    <Link to={`/settings/users/${user.userId || user.user_id || user.id}/reset-password`}>
                      Reset
                    </Link>
                    <button onClick={() => handleDelete(user.userId || user.user_id || user.id, user.email)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Page {page} of {totalPages} • {totalUsers} users • Sorted by {sortBy} ({sortDir})
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => changePage(1)} disabled={!canPrev}>First</button>
          <button onClick={() => changePage(page - 1)} disabled={!canPrev}>Prev</button>
          <button onClick={() => changePage(page + 1)} disabled={!canNext}>Next</button>
          <button onClick={() => changePage(totalPages)} disabled={!canNext}>Last</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-xl)' }}>
        <div className="info-panel">
          <h4>User Statistics</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)', fontSize: '0.875rem' }}>
            <div>
              <strong>Total Users:</strong> {totalUsers}
            </div>
            <div>
              <strong>Active:</strong> {users.filter(u => (u.isActive ?? u.is_active) !== false).length}
            </div>
            <div>
              <strong>Admins:</strong> {users.filter(u => u.roles?.some(r => (r.role_name || r.roleName) === 'admin')).length}
            </div>
            <div>
              <strong>Recent:</strong> {users.filter(u => {
                const lastLogin = u.lastLogin || u.lastLoginAt || u.last_login_at;
                if (!lastLogin) return false;
                const daysSinceLogin = (new Date() - new Date(lastLogin)) / (1000 * 60 * 60 * 24);
                return daysSinceLogin <= 7;
              }).length}
            </div>
          </div>
        </div>

        <div className="info-panel">
          <h4>About User Management</h4>
          <p>
            Manage all users in your system. You can create new users, edit existing ones, assign roles, 
            reset passwords, and monitor user activity. Users inherit permissions based on their assigned roles.
          </p>
        </div>
      </div>
    </div>
  );
}

export default UserList;